import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import { action, reaction, makeObservable } from "mobx";
import {IconTitleTextTooltip} from "../../components/tooltip/tooltips/icon-title-text/icon-title-text.tooltip";
import React from "react";
import {IFreePlayVoucherViewModel} from "./vouchers/free-play-voucher-view-model.interface";
import {IFreePlayService} from "./free-play.service.interface";
import {ITooltipViewModel} from "../tooltip/tooltip-view-model.interface";
import {TimeSpan} from "@sparkware/app-utils";
import {FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER, FREE_PLAY_TOOLTIP_IS_DISPLAYED_IN_SESSION} from "./free-play-constants";


export class FreePlayTooltipNotificationHandler {
    /*
        The freePlayService is passed explicitly here in the constructor because
        FreePlayTooltipNotificationHandler is instantiated in the FreePlayService constructor
        and if we use services.freePlay we will cause a circular dependency error.
     */
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly freePlayService: IFreePlayService) {
        makeObservable(this);

        reaction(() => freePlayService.userVouchers,
            (vouchers) => {

                let hasNewVouchers = false;

                vouchers.forEach(v => {
                    if(!this._alreadyNotifiedVouchers[v.id]) {
                        hasNewVouchers = true;
                        this._alreadyNotifiedVouchers[v.id] = v;
                    }
                })

                if(hasNewVouchers) {
                    this._showTooltipOnFreePlay();
                }
            });

        reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if (!isAuthorized) {
                    this.services.localStorage.setItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED_IN_SESSION, false);
                    this._alreadyNotifiedVouchers = {};
                    this._closeTooltip();
                }
            }, {
                fireImmediately: true
            });
    }

    private _toolTip: ITooltipViewModel | null = null;
    private _freePlayIndicatorElementRef: HTMLDivElement | null = null;
    private _alreadyNotifiedVouchers: Record<string, IFreePlayVoucherViewModel> = {};

    private _closeTooltip(): void {
        this._toolTip?.closeTooltip();
        this._toolTip = null;
    }

    setFreePlayIndicatorDOMElementRef(element: HTMLDivElement | null): void {
        this._freePlayIndicatorElementRef = element;
        if (!element) {
            this._closeTooltip();
        }
    }

    private get _toolTipWasDisplayedOnCurrentSession(): boolean {
        return this.services.localStorage.getItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED_IN_SESSION) === "true";
    }

    private get _toolTipDisplayCount(): number {
        return parseInt(this.services.localStorage.getItem(FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER) || '0')
    }

    @action.bound
    private _onTooltipClicked() {
        this._closeTooltip();
        this.services.userMenu.openRewards();
        this.services.analytics.sendOrbitUserFlowEvent("Tooltip", "Click", "FP_Bubble");
    }

    private _shouldShowTooltip() {
        if (!this.services.freePlay.hasVouchers || !this._freePlayIndicatorElementRef) {
            return false;
        }


        return !(this._toolTipDisplayCount >= 5
            || this._toolTipWasDisplayedOnCurrentSession
            || !this.services.routing.isOrbitRoute(this.services.location.currentUri.pathName)
            || this.services.routing.currentRoute.data.preventNotifications
            || this.services.sdk.isGameRunning());
    }

    // Show tooltip for 7 sec, 5 times only and max 1 time each login session.
    private _showTooltipOnFreePlay() {
        if (!this._shouldShowTooltip()) {
            return;
        }

        const translateService = this.services.translation;
        const FPGiftIcon = this.services.theme.currentTheme.images.tooltip.giftIcon.toString();

        this._toolTip = this.services.tooltip.createOnBoardingTooltip({
                content: (<IconTitleTextTooltip icon={FPGiftIcon}
                                                title={translateService.translate("CONGRATS")}
                                                text={translateService.translate("YOUR_FP_ARE_WAITING")}
                                                onClick={this._onTooltipClicked}/>),
                attachTo: this._freePlayIndicatorElementRef,
                timeout: TimeSpan.fromSeconds(7),
                onTooltipRendered: ()=> this._onTooltipRendered()
        });
    }

    private _onTooltipRendered() {
        this.services.analytics.sendOrbitUserFlowEvent("Tooltip", "Show", "FP_Bubble");
        this.services.localStorage.setItem(FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER, this._toolTipDisplayCount + 1);
        this.services.localStorage.setItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED_IN_SESSION, true);
    }
}
